/**
 * The well form, used full-page by New Well and as the edit panel on a well's
 * own page (no modal: a well is a record).
 *
 * IDENTIFIER BY COUNTRY: a US tenant types the API number, every other tenant
 * the UWI. The other identifier is shown too when the well already holds one,
 * so a value is never kept invisibly. Both are free text as written — the API
 * enforces no format, only uniqueness (a 409 naming the existing well).
 *
 * A blank field is sent as null (absent), never as 0 or "". On edit only the
 * keys that changed are sent; PATCH keeps the rest.
 *
 * Checked here before sending, each with a sentence: a customer, a well name,
 * latitude / longitude in range, expected H2S a number of ppm, 0 or more. The
 * API checks them all again.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ProvinceSelect from '../../components/ProvinceSelect';
import { blankToNull, toInput, wtIsUs } from '../../lib/well-testing';
import type { WtCatalog, WtMeta, WtWell, WtWellWrite } from '../../types/well-testing';
import { WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtField, WtFormError } from './WtBits';

export interface WellFormState {
  customer_id: string;
  well_name: string;
  uwi: string;
  api_number: string;
  licence_number: string;
  surface_location: string;
  province_state: string;
  latitude: string;
  longitude: string;
  field_name: string;
  pool_name: string;
  fluid_type: string;
  sour: boolean;
  expected_h2s_ppm: string;
  notes: string;
}

export const wellFormState = (w: WtWell | null): WellFormState => ({
  customer_id: w?.customerId ?? '',
  well_name: w?.wellName ?? '',
  uwi: w?.uwi ?? '',
  api_number: w?.apiNumber ?? '',
  licence_number: w?.licenceNumber ?? '',
  surface_location: w?.surfaceLocation ?? '',
  province_state: w?.provinceState ?? '',
  latitude: toInput(w?.latitude),
  longitude: toInput(w?.longitude),
  field_name: w?.fieldName ?? '',
  pool_name: w?.poolName ?? '',
  fluid_type: w?.fluidType ?? '',
  sour: w?.sour === true,
  expected_h2s_ppm: toInput(w?.expectedH2sPpm),
  notes: w?.notes ?? '',
});

const DECIMAL = /^-?\d+(\.\d+)?$/;

/** The first thing wrong with the form, as a sentence; null when it may be sent. */
export function wellFormError(form: WellFormState): string | null {
  if (!form.customer_id) return 'Pick the customer this well belongs to.';
  if (!form.well_name.trim()) return 'Give the well a name.';
  const coord = (text: string, label: string, limit: number) => {
    const t = text.trim();
    if (t === '') return null;
    if (!DECIMAL.test(t) || Math.abs(Number(t)) > limit) return `${label} must be a number between -${limit} and ${limit}.`;
    return null;
  };
  const lat = coord(form.latitude, 'Latitude', 90);
  if (lat) return lat;
  const lng = coord(form.longitude, 'Longitude', 180);
  if (lng) return lng;
  const h2s = form.expected_h2s_ppm.trim();
  if (h2s !== '' && (!/^\d+(\.\d+)?$/.test(h2s))) return 'Expected H2S must be a number of ppm, 0 or more.';
  return null;
}

/** The write body: every filled field on create, only the changed ones on edit. */
export function wellWriteBody(form: WellFormState, initial: WellFormState | null): WtWellWrite {
  const body: Record<string, unknown> = {};
  (Object.keys(form) as (keyof WellFormState)[]).forEach((k) => {
    if (initial && form[k] === initial[k]) return;
    const v = form[k];
    if (typeof v === 'boolean') body[k] = v;
    else if (k === 'customer_id') body[k] = v;
    else if (k === 'well_name') body[k] = v.trim();
    else body[k] = blankToNull(v);
  });
  if (!initial) {
    // A create sends no empty optional field at all: absent and null mean the same to the API.
    Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
  }
  return body as WtWellWrite;
}

export function WellForm({
  well,
  catalog,
  meta,
  customers,
  pending,
  error,
  duplicateOf,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  well: WtWell | null;
  catalog: WtCatalog | null;
  meta: WtMeta | null;
  customers: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  /** The existing well a 409 duplicate_well named, linked under the error. */
  duplicateOf?: string | null;
  submitLabel: string;
  onSubmit: (body: WtWellWrite) => void;
  onCancel: () => void;
}) {
  const initial = useMemo(() => (well ? wellFormState(well) : null), [well]);
  const [form, setForm] = useState<WellFormState>(() => wellFormState(well));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof WellFormState>(k: K, v: WellFormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const us = wtIsUs(meta);
  const country = us ? 'US' : 'CA';
  // The tenant's identifier always; the other one only when this well already holds a value.
  const showUwi = !us || !!initial?.uwi;
  const showApi = us || !!initial?.api_number;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const problem = wellFormError(form);
    if (problem) return setLocalError(problem);
    const body = wellWriteBody(form, initial);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="wt-well-form" noValidate>
      <WtFormError message={localError ?? error} />
      {!localError && error && duplicateOf && (
        <p className="text-sm" data-testid="wt-duplicate-link">
          <Link to={`/well-testing/wells/${duplicateOf}`} className="font-semibold text-fieldiq-text underline">
            Open the existing well
          </Link>
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <WtField label="Customer">
          <select className={WT_INPUT} value={form.customer_id} onChange={(e) => set('customer_id', e.target.value)} aria-label="Customer">
            <option value="">Select a customer…</option>
            {/* The well's own customer stays selectable even if the list could not load. */}
            {well && !customers.some((c) => c.id === well.customerId) && (
              <option value={well.customerId}>{well.customerName ?? '—'}</option>
            )}
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Well name">
          <input className={WT_INPUT} value={form.well_name} onChange={(e) => set('well_name', e.target.value)} aria-label="Well name" />
        </WtField>
        {showUwi && (
          <WtField label="UWI" hint="As written on the licence, e.g. 100/06-12-045-08W5/00">
            <input className={WT_INPUT} value={form.uwi} onChange={(e) => set('uwi', e.target.value)} aria-label="UWI" />
          </WtField>
        )}
        {showApi && (
          <WtField label="API number" hint="e.g. 42-389-12345">
            <input className={WT_INPUT} value={form.api_number} onChange={(e) => set('api_number', e.target.value)} aria-label="API number" />
          </WtField>
        )}
        <WtField label="Licence number">
          <input className={WT_INPUT} value={form.licence_number} onChange={(e) => set('licence_number', e.target.value)} aria-label="Licence number" />
        </WtField>
        <WtField label="Surface location" hint={us ? 'Section / block / survey' : 'LSD, e.g. 06-12-045-08W5'}>
          <input className={WT_INPUT} value={form.surface_location} onChange={(e) => set('surface_location', e.target.value)} aria-label="Surface location" />
        </WtField>
        <WtField label={us ? 'State' : 'Province'}>
          <ProvinceSelect
            country={country}
            value={form.province_state}
            onChange={(v) => set('province_state', v)}
            className={WT_INPUT}
            aria-label={us ? 'State' : 'Province'}
          />
        </WtField>
        <WtField label="Latitude">
          <input className={WT_INPUT} inputMode="decimal" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} aria-label="Latitude" />
        </WtField>
        <WtField label="Longitude">
          <input className={WT_INPUT} inputMode="decimal" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} aria-label="Longitude" />
        </WtField>
        <WtField label="Field">
          <input className={WT_INPUT} value={form.field_name} onChange={(e) => set('field_name', e.target.value)} aria-label="Field" />
        </WtField>
        <WtField label="Pool">
          <input className={WT_INPUT} value={form.pool_name} onChange={(e) => set('pool_name', e.target.value)} aria-label="Pool" />
        </WtField>
        <WtField label="Fluid">
          <select className={WT_INPUT} value={form.fluid_type} onChange={(e) => set('fluid_type', e.target.value)} aria-label="Fluid">
            <option value="">Not set</option>
            {(catalog?.fluidTypes ?? []).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </WtField>
        <WtField label="Expected H2S (ppm)" hint="Blank when not known">
          <input
            className={WT_INPUT}
            inputMode="decimal"
            value={form.expected_h2s_ppm}
            onChange={(e) => set('expected_h2s_ppm', e.target.value)}
            aria-label="Expected H2S (ppm)"
          />
        </WtField>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
          <input type="checkbox" checked={form.sour} onChange={(e) => set('sour', e.target.checked)} />
          Sour well (H2S)
        </label>
      </div>
      <WtField label="Notes">
        <textarea className={`${WT_INPUT} h-24 py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WT_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
